import React from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Select,
  Switch,
  Text,
  TextField,
} from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';

import { FormField, MultiSelect, QuillEditor } from 'shared/components';
import { AutocompleteV2 } from 'shared/components/autocomplete-v2';
import { AddIcon, ClearIcon, SearchIcon } from 'shared/icons';

import { useCreateResumeContext } from '../create-resume.context';
import { getRequiredFieldError } from '../create-resume.validation';
import {
  AdditionalInfoStore,
  CompanyMultiAutocomplete,
} from './additonal-info.store';

type AdditionalInfoProps = {
  renderSteps: (valid: boolean) => React.ReactNode;
  showErrors: boolean;
};

const CompanySelector = observer(
  ({
    label,
    store,
    showErrors,
  }: {
    label: string;
    store: CompanyMultiAutocomplete;
    showErrors: boolean;
  }) => (
    <FormField
      label={label}
      required
      error={getRequiredFieldError(showErrors, store.valid)}
    >
      <Flex direction="column" gap="2">
        {store.selected.length ? (
          <Flex gap="1" wrap="wrap">
            {store.selected.map((company) => (
              <Badge key={company.id} color="blue" highContrast>
                <Text truncate>{company.name}</Text>
                <IconButton
                  type="button"
                  size="1"
                  variant="surface"
                  color="blue"
                  onClick={() => {
                    store.remove(company);
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </Badge>
            ))}
          </Flex>
        ) : null}
        <AutocompleteV2.Root
          items={store.autocompleteOptions}
          viewModel={store.autocompleteVM}
          getOptionValue={(company) => company.id}
          getOptionLabel={(company) => company.name}
          color="indigo"
          indicator={<SearchIcon width={20} height={20} />}
          rotateIndicatorOnOpen={false}
          loading={store.loading}
          placeholder="Введіть організацію"
          resetInputOnClose="always"
          onSearch={store.onSearch}
          onSelect={store.onSelect}
        />
      </Flex>
    </FormField>
  )
);

const LanguageRows = observer(
  ({
    additionalInfo,
    showErrors,
  }: {
    additionalInfo: AdditionalInfoStore;
    showErrors: boolean;
  }) => (
    <Flex direction="column" gap="3">
      {additionalInfo.languages.map((language, index) => (
        <Box
          key={language.id}
          p="4"
          style={{ border: '1px solid var(--gray-a6)', borderRadius: 8 }}
        >
          <Flex direction="column" gap="4">
            <Flex justify="between" align="center">
              <Text weight="medium">Мова</Text>
              <IconButton
                type="button"
                variant="soft"
                color="red"
                disabled={additionalInfo.languages.length === 1}
                onClick={() => {
                  additionalInfo.removeLanguage(language.id);
                }}
              >
                <ClearIcon />
              </IconButton>
            </Flex>

            <Flex gap="4">
              <FormField
                label="Мова"
                required={language.hasValue}
                error={getRequiredFieldError(
                  showErrors,
                  !language.hasValue || Boolean(language.languageId)
                )}
              >
                <Select.Root
                  size="3"
                  value={language.languageId}
                  onValueChange={(value) => {
                    additionalInfo.setLanguageId(language.id, value);
                  }}
                >
                  <Select.Trigger color="indigo" placeholder="Оберіть мову" />
                  <Select.Content color="indigo" position="popper">
                    {additionalInfo.languageOptions.map((option) => (
                      <Select.Item key={option.id} value={String(option.id)}>
                        {option.name}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </FormField>

              <FormField
                label="Рівень володіння"
                required={language.hasValue}
                error={getRequiredFieldError(
                  showErrors,
                  !language.hasValue || Boolean(language.skillId)
                )}
              >
                <Select.Root
                  size="3"
                  value={language.skillId}
                  onValueChange={(value) => {
                    additionalInfo.setLanguageSkillId(language.id, value);
                  }}
                >
                  <Select.Trigger color="indigo" placeholder="Оберіть рівень" />
                  <Select.Content color="indigo" position="popper">
                    {additionalInfo.languageSkillOptions.map((option) => (
                      <Select.Item key={option.id} value={String(option.id)}>
                        {option.name}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </FormField>
            </Flex>

            {index === additionalInfo.languages.length - 1 &&
            additionalInfo.languages.length < 5 ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  additionalInfo.addLanguage();
                }}
              >
                Додати ще <AddIcon />
              </Button>
            ) : null}
          </Flex>
        </Box>
      ))}
    </Flex>
  )
);

const AdditionalInfoView = ({ renderSteps, showErrors }: AdditionalInfoProps) => {
  const { additionalInfo } = useCreateResumeContext();

  return (
    <form>
      <Box>
        <Flex direction="column" gap="5" my="4">
          <FormField label="Про себе">
            <QuillEditor
              value={additionalInfo.aboutYourself}
              minHeight={120}
              placeholder="Розкажіть про себе"
              onValueChange={additionalInfo.setAboutYourself}
            />
          </FormField>

          <FormField label="Video CV">
            <TextField.Root
              size="3"
              color="indigo"
              value={additionalInfo.videoCvLink}
              placeholder="https://..."
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                additionalInfo.setVideoCvLink(event.target.value);
              }}
            />
          </FormField>

          <FormField label="Ключові навички">
            <MultiSelect.Root
              vm={additionalInfo.skills}
              placeholder="Оберіть ключові навички"
            >
              {additionalInfo.filteredSkills.map((skill) => (
                <MultiSelect.Option key={skill.id} value={skill}>
                  {skill.name}
                </MultiSelect.Option>
              ))}
            </MultiSelect.Root>
          </FormField>

          <FormField label="Мови">
            <LanguageRows additionalInfo={additionalInfo} showErrors={showErrors} />
          </FormField>

          <Flex direction="column" gap="4">
            <Flex align="center" gap="2">
              <Switch
                checked={additionalInfo.driverLicense}
                onCheckedChange={additionalInfo.setDriverLicense}
              />
              <Text size="2">Маю водійське посвідчення</Text>
            </Flex>

            {additionalInfo.driverLicense ? (
              <FormField
                label="Категорія водійського посвідчення"
                required
                error={getRequiredFieldError(
                  showErrors,
                  additionalInfo.driverCategories.values.length > 0
                )}
              >
                <MultiSelect.Root
                  vm={additionalInfo.driverCategories}
                  placeholder="Оберіть категорію"
                >
                  {additionalInfo.filteredDriverCategories.map((category) => (
                    <MultiSelect.Option key={category.id} value={category}>
                      {category.name}
                    </MultiSelect.Option>
                  ))}
                </MultiSelect.Root>
              </FormField>
            ) : null}
          </Flex>

          <FormField
            label="Видимість резюме"
            required
            error={getRequiredFieldError(showErrors, Boolean(additionalInfo.visibility))}
          >
            <Select.Root
              size="3"
              value={additionalInfo.visibility?.value ?? ''}
              onValueChange={additionalInfo.setVisibility}
            >
              <Select.Trigger color="indigo" placeholder="Оберіть видимість" />
              <Select.Content color="indigo" position="popper">
                {additionalInfo.visibilityOptions.map((option) => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </FormField>

          {additionalInfo.isWhitelistVisibility ? (
            <CompanySelector
              label="Організації, яким видно резюме"
              store={additionalInfo.companyWhitelist}
              showErrors={showErrors}
            />
          ) : null}

          {additionalInfo.isBlacklistVisibility ? (
            <CompanySelector
              label="Організації, яким не видно резюме"
              store={additionalInfo.companyBlacklist}
              showErrors={showErrors}
            />
          ) : null}
        </Flex>
      </Box>

      <Flex mt="4">{renderSteps(additionalInfo.valid)}</Flex>
    </form>
  );
};

export const AdditionalInfo = observer(AdditionalInfoView);
